//================================================================
//  Component: Publish To Inventory
//================================================================

//  Purpose: Contains form in Final Assessment Tab to publish an application to Inventory. Only visible in New Initiative or Technology requests

//  Properties:
//  - taskSection = {useState, determines whether this section is collapsed}
//  - task = {object, holds task data}
//  - setPageStatus = {useState, see page container component}
//  - preventSubmit = {useState, disables submission once the request is complete}

//  Example:
//  
//      <PublishToInventory
//            taskSection={taskSection}
//            task={task}
//            setPageStatus={setPageStatus}
//            preventSubmit={readyToSubmit}
//        ></PublishToInventory>
//================================================================

// Libraries
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router'
import { useNavigate } from 'react-router-dom';

// Contexts
import { GetUser } from '../../../../../Library/GlobalContexts';

// Functions
import QueryDocument from '../../../../../Library/QueryDocument';
import WriteDocument from '../../../../../Library/WriteDocument';
import QueryListener from '../../../../../Library/QueryListener';
import AddRemoveDocumentArray from '../../../../../Library/AddRemoveDocumentArray';

// Components
import LiveField from '../../../../../Components/LiveField/LiveField';
import Tooltip from '../../../../../Components/Tooltip/Tooltip';

// Images


export default function PublishToInventory({
    taskSection,
    task,
    setPageStatus,
    preventSubmit,
}) {

    //------------------------------------------------------
    //  useContexts
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  React Router
    //------------------------------------------------------

    const navigate = useNavigate()
    const routerUseLocation = useLocation();

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to determine if the confirmation modal is visible (stores the pending demand management tasks)
    const [pendingDemandManagementRequests, setPendingDemandManagementRequests] = useState([]);

    // --------------------------------------------------
    //  Functions
    // --------------------------------------------------

    // Returns true if "Complete Request" button should be disabled
    const isDisabled = () => {

        if (preventSubmit.length > 0 ||
            pendingDemandManagementRequests?.length > 0 ||
            (task?.publishtoinventory?.applicationname?.length === 0 && task?.applicationname?.length === 0) ||
            task?.publishtoinventory?.applicationdescription?.length === 0 ||
            task?.publishtoinventory?.businessowner === undefined || task?.publishtoinventory?.businessowner?.email?.length === 0 ||
            task?.publishtoinventory?.itowner === undefined || task?.publishtoinventory?.itowner?.email?.length === 0) return true;

        return false;

    }

    // Submit Handler > Close Ticket & Add Application to Inventory
    const onSubmit = () => {

        // ===============================================
        //  Check for any pending business partner requests 
        // ===============================================

        setPageStatus('pending');

        // ==========================================
        //  Create a searcharray for lookup fields
        // ==========================================

        const searchArray = [];
        let previousValue = '';
        const applicationname = task?.publishtoinventory?.applicationname === '' ? task?.applicationname : task?.publishtoinventory?.applicationname;
        [...applicationname]?.forEach((value, index) => {

            // Skip the first character
            if (index === 0) {

                previousValue = value;
                return searchArray.push(value.toLowerCase());

            }

            // Create an array of each character
            searchArray.push(`${previousValue}${value}`.toLowerCase());
            previousValue = `${previousValue}${value}`.toLowerCase();

        });

        // ===========================================
        //  Look Up Agreement
        // ===========================================

        QueryDocument('agreements', [['requestid', 'array-contains', task?.requestid]])
            .then((agreement) => {

                // ===========================================
                //  Write Application Doc to Firestore
                // ===========================================

                const docPromises = [];

                const applicationId = `${btoa(applicationname)}-${Date.now()}`;

                // Get the ealiest contract end date by sorting the agreements by agreementenddate
                const earliestcontractenddate = agreement.sort((a, b) => Date.parse(a.agreementenddate) - Date.parse(b.agreementenddate))[0];

                const documentObject = {
                    'applicationid': applicationId,
                    'requestid': [task?.requestid],
                    'linkedrequests': {},
                    'applicationstatus': 'Active',
                    'createdby': getUser.emailaddress,
                    'createdbyname': `${getUser.givenname} ${getUser.surname}`,
                    'createddate': new Date(),
                    'lastmodifieddate': new Date(),
                    'lastmodifiedby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname,
                    },
                    'agreements': [],
                    'applicationname': applicationname,
                    'searcharray': searchArray,
                    'applicationdescription': task?.publishtoinventory?.applicationdescription === '' ? task?.applicationdescriptionbn : task?.publishtoinventory?.applicationdescription,
                    'vendor': {
                        'vendorname': '',
                        'vendorid': '',
                    },
                    'itowner': [
                        task?.publishtoinventory?.itowner?.email
                    ],
                    'businessowner': [
                        task?.publishtoinventory?.businessowner?.email
                    ],
                    'defaultapp': [],
                    'usedby': [],
                    'subscribed': [],
                    'nooflicensespurchased': '0',
                    'type': '',
                    'deploymentname': '',
                    'deploymenttype': [],
                    'capabilityfamily': [],
                    'supportedby': '',
                    'supportlevelinternal': [],
                    'supportlevelexternal': [],
                    'ssoimplemented': '',
                    'mfaimplemented': '',
                    'typeofcost': '',
                    'budgetowner': [],
                    'projectcode': task?.projectcode,
                    'tasknumber': task?.tasknumber,
                    'expendituretype': task?.expendituretype,
                    'licencetype': '',
                    'costallocationcurrency': '',
                    'bcp': '',
                    'rpo': '',
                    'rto': '',
                    'linktodr': '',
                    'region': task?.region,
                    'applicationcategories': [],
                    'primarybeneficiary': task?.primaryusers,
                    'itmanaged': 'Yes',
                    'itcommerciallymanaged': 'Yes',
                    'itbudgeted': 'Yes',

                    'environment': [],   // 'Prod', 'UAT/pre-prod', 'SIT', 'Dev', 'DR'
                    'appserver': '',
                    'otherserver': '',
                    'softwarerequirements': '',
                    'dnsnames': [],
                    'serviceaccounts': [],
                    'gcpproject': [],
                    'primaryhostinglocation': '',
                    'secondaryhostinglocation': '',
                    'applicationurl': '',
                    'osversion': '',
                    'mtohours': '', //  Choice - 'Tier 0', 'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5', 'Tier 6'  
                    'drfrequency': '', // Choice -  'Annually, Semi-Annually 
                    'applicablecontrols': '',
                    'dataclassification': '', // Choice - 'Public', 'Internal', 'Confidential', 'Restricted'
                    'contractenddate': earliestcontractenddate ? earliestcontractenddate.agreementenddate : '',
                    'numberofusers': '', // Number
                    'visibleinjsm': '', // Choice - 'Yes', 'No'
                    'supportassignmentgroup': '',
                    'supportrenewaldate': '',
                    'workflows': '',
                    'useraccessreview': '', // Choice - 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
                    'prodbackup': '', // Choice - 'Daily', 'Weekly', 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
                    'slareporting': '', // Choice - 'Monthly' 'Quarterly' 'Semi-Annual' or 'Annual'
                    'supportdl': '',
                    'decommissiondata': {
                        'decommissiondate': null,
                        'dataretention': '',
                        'dataclassification': '',
                        'datalocation': '', // Choice - 'Sharepoint', 'DAS', 'Common Vault', 'tape'
                    },
                    'nextcompreviewdate': null, // Date
                    'groups': [],
                    'assettype': '',
                    'mva': '',
                    'confidentialityrating': '', // Choice - 1 to 5
                    'integrityrating': '', // Choice - 1 to 5
                    'availabilityrating': '', // Choice - 1 to 5
                    'sensitivityrating': '', // Choice - 1 to 5
                    'assetscore': '',  // 0 to 100
                    'assetier': '', // Choice - 1 to 5
                    'internetfacing': '', // Choice - Yes, No & N/A 
                };

                docPromises.push(
                    WriteDocument('applications', documentObject.applicationid, documentObject, false)
                );

                // ===========================================
                //  Close the Digital Request Ticket
                // ===========================================

                const updatedDoc = {
                    'applicationid': applicationId,
                    'status': 'Completed',
                    'lastmodifieddate': new Date(),
                    'lastmodifiedby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'activityfeed': task?.activityfeed,
                    'stage': 'Completed',
                }


                docPromises.push(
                    WriteDocument('requests', task.requestid, updatedDoc, true)
                );

                // Update the activity feed
                const updatedActivityFeed = {
                    'activitydate': new Date(),
                    'actionedby': {
                        'email': getUser?.emailaddress,
                        'givenname': getUser?.givenname,
                        'surname': getUser?.surname
                    },
                    'action': 'completed',
                    'comments': '',
                }

                docPromises.push(
                    AddRemoveDocumentArray('requests', task.requestid, 'activityfeed', updatedActivityFeed, 'add', updatedActivityFeed.actionedby)
                )

                // ===========================================
                //  Resolve Promises
                // ===========================================

                return Promise.all(docPromises)
                    .then(() => {

                        setPageStatus('onload');
                        setPendingDemandManagementRequests([]);

                        navigate(`/${routerUseLocation.pathname.split('/')[1]}/${routerUseLocation.pathname.split('/')[2]}?view=triage`);

                        // Scroll to the top of the page
                        const body = document.querySelector('.PageComponent-Container');

                        body.scrollIntoView({
                            behavior: 'smooth'
                        }, 300);

                    })

            })
            .catch((errors) => {

                setPageStatus('error-fatal');
                console.log('error', errors);

            })

    }

    // --------------------------------------------------
    //  useEffects
    // --------------------------------------------------


    // Query Listener
    // - Check if there are any pending 'Diligence and Governance' requests
    useEffect(() => {

        if (task?.parentrequestid === undefined) return;

        function onLoadChange(document) {
            setPendingDemandManagementRequests(document);

        }

        function onError(error) {
            console.log('error', error);

        }

        const unsubscribe = QueryListener('requests', [
            ['parentrequestid', '==', task?.parentrequestid],
            ['status', '==', 'In Progress'],
            ['tasktype', 'not-in', ['Digital Request']],
        ], onLoadChange, onLoadChange, onError,);

        return () => {
            unsubscribe();
        };

    }, [task]);

    // --------------------------------------------------
    //  HTML
    // --------------------------------------------------

    return (
        <div className='flex flex-col gap-2 p-0' hidden={taskSection?.publishToInventoryCollapsed}>

            <div className='w-full flex flex-col gap-2 overflow-hidden p-[3%]'>

                <p className='m-0'>
                    Following the completion of this request, the requestor will be notified of the outcome.
                </p>

                {/* =========================================================== */}
                {/*  1. Application Name                                        */}
                {/* =========================================================== */}

                <LiveField
                    type={'text'}
                    collectionid={'requests'}
                    documentid={task?.requestid}
                    fieldpath={'publishtoinventory.applicationname'}
                    defaultValue={task?.publishtoinventory?.applicationname === '' ? task?.applicationname : task?.publishtoinventory?.applicationname}
                    placeholder={'Enter an application name'}
                    label={<label className='font-medium'> 1. Application Name <span className='text-[#C4314B]'>*</span></label>}
                    disabled={task?.status !== 'In Progress'}
                ></LiveField>

                {/* =========================================================== */}
                {/*  2. Application Description                                 */}
                {/* =========================================================== */}

                <LiveField
                    type={'multiline'}
                    collectionid={'requests'}
                    documentid={task?.requestid}
                    fieldpath={'publishtoinventory.applicationdescription'}
                    label={<label className='font-medium'> 2. Application Description <span className='text-[#C4314B]'>*</span></label>}
                    defaultValue={task?.publishtoinventory?.applicationdescription === '' ? task?.applicationdescriptionbn : task?.publishtoinventory?.applicationdescription}
                    placeholder={'Enter a description'}
                    disabled={task?.status !== 'In Progress'}
                ></LiveField>

                {/* =========================================================== */}
                {/*  3. Business Owner                                          */}
                {/* =========================================================== */}

                <LiveField
                    type={'user-search'}
                    collectionid={'requests'}
                    documentid={task?.requestid}
                    fieldpath={'publishtoinventory.businessowner'}
                    label={<label className='font-medium'> 3. Business Owner <span className='text-[#C4314B]'>*</span> </label>}
                    defaultValue={task?.publishtoinventory?.businessowner}
                    disabled={task?.status !== 'In Progress'}
                ></LiveField>

                {/* =========================================================== */}
                {/*  4. IT Owner                                                */}
                {/* =========================================================== */}

                <LiveField
                    type={'user-search'}
                    collectionid={'requests'}
                    documentid={task?.requestid}
                    fieldpath={'publishtoinventory.itowner'}
                    label={<label className='font-medium'> 4. IT Owner <span className='text-[#C4314B]'>*</span> </label>}
                    defaultValue={task?.publishtoinventory?.itowner}
                    disabled={task?.status !== 'In Progress'}
                ></LiveField>

                {/* =========================================================== */}
                {/*  Submit Button                                              */}
                {/* =========================================================== */}

                <button
                    className='Primary-Button w-40 mt-4'
                    onClick={onSubmit}
                    disabled={isDisabled()}
                >
                    {
                        !isDisabled() ? 'Complete Request' :
                            <Tooltip
                                children={'Complete Request'}
                                message={
                                    <ul className='list-disc absolute rounded text-left top-0 left-0 py-2 pr-2 bg-gray-800'>
                                        {preventSubmit.map((blocker, index) => (
                                            <li key={index}> {blocker} </li>
                                        ))}
                                    </ul>
                                }
                            ></Tooltip>
                    }
                </button>
                <p className='font-light italic' >All final approvals must be secured prior to completing the request. Additionally, any outstanding requests in 'Diligence & Governance' must be fully addressed before advancing to the next stage.</p>

            </div>

        </div>
    );
}
